import { getRootContainerProps } from "@client/content/lib/helper";
import { ModuleSecondaryButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import Icon from "@client/controls/Icon/component";
import DeleteModal from "@client/controls/Modal/Modals/Delete/component";
import date from "@client/lib/date";
import { IconType, type OptionItem } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import AddEditModal from "../AddEditModal/container";
import { Approved, Forum, PostAvatar } from "../Components";
import { scrollToEl } from "../lib";
import { type Forums } from "../types";
import Actions from "./Actions";
import CommentForm from "./CommentForm/container";
import Comments from "./Comments/container";

export type MapProps = RootContainerProps & {
  activeAction: Forums.View.Action | null;
  apiInfo: ModuleApiInfo | null;
  forumOptions: OptionItem<string>[];
  item: Forums.Post;
  status: string;
  userId?: number | null;
};

export type ActionProps = {
  onBack: () => void;
  onDeleteClick: (ticketId: string) => void;
  onCancelAction: () => void;
  onConfirmAction: (data?: any) => void;
  onSubmit: (data: Forums.View.AddEditPost) => void;
  onReplyCommentClick: (ticketCommentId: string) => void;
  onUpdateClick: (ticketId: string) => void;
  onUpdateCommentClick: (ticketCommentId: string) => void;
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const formRef = React.useRef<HTMLDivElement>(null);

  const onReplyClick = React.useCallback(() => {
    scrollToEl(formRef.current);
  }, []);

  const onReplyCommentClick = React.useCallback(
    (ticketCommentId: string) => {
      props.onReplyCommentClick(ticketCommentId);

      scrollToEl(formRef.current);
    },
    [props.onReplyCommentClick]
  );

  const onUpdateCommentClick = React.useCallback(
    (ticketCommentId: string) => {
      props.onUpdateCommentClick(ticketCommentId);

      scrollToEl(formRef.current);
    },
    [props.onUpdateCommentClick]
  );

  return (
    <div className="detail">
      <div className="header">
        <ModuleSecondaryButton
          {...getRootContainerProps(props)}
          caption={i18n.allPosts}
          className="all_posts_btn"
          icon={IconType.LongArrowLeft}
          onClick={props.onBack}
        />
      </div>
      <div className="post">
        <div className="post_info">
          <PostAvatar post={props.item} />
          <Forum color={props.item.forumColor} name={props.item.forumName} />
          <span>{date.formatDateTime(props.item.createdOnDate)}</span>
        </div>

        <h3 className="post_header">{props.item.subject}</h3>

        <div className="post_body" dangerouslySetInnerHTML={{ __html: props.item.message }} />

        <div className="post_footer">
          {!!props.userId && (
            <div className="item reply" onClick={onReplyClick}>
              <Icon iconClassName={IconType.Comment} /> {i18n.reply}
            </div>
          )}
          {props.item.contactUserId === props.userId && (
            <>
              <Actions
                onDeleteClick={() => props.onDeleteClick(props.item.ticketId)}
                onUpdateClick={() => props.onUpdateClick(props.item.ticketId)}
              />
              <Approved isApproved={props.item.isApproved} />
            </>
          )}
        </div>
      </div>

      <Comments
        {...getRootContainerProps(props)}
        onReplyClick={onReplyCommentClick}
        onUpdateClick={onUpdateCommentClick}
      />

      {!!props.userId && <CommentForm ref={formRef} {...getRootContainerProps(props)} />}

      {props.activeAction?.type === "update" && props.activeAction?.target === "post" && (
        <AddEditModal
          {...getRootContainerProps(props)}
          onClose={props.onCancelAction}
          onSubmit={props.onConfirmAction}
        />
      )}

      <DeleteModal
        onClose={props.onCancelAction}
        onDelete={props.onConfirmAction}
        isVisible={props.activeAction?.type === "delete"}
        title={props.activeAction?.target === "comment" ? i18n.deleteComment : i18n.deletePost}
        message={
          props.activeAction?.target === "comment" ? i18n.infoDeleteCommentConfirmation : i18n.infoDeletePostConfirmation
        }
      />
    </div>
  );
};
